import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createBatch } from "../pages/services/batchService";
import NotesTable from "./NotesTable";
import "../styles/AddForm.css";
import "../styles/AddBatches.css";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const AddBatches = () => {
  const [formData, setFormData] = useState({
    batchTitle: "", batchDuration: "", batchDescription: "",
    trainerId: "", batchStartTime: "", batchEndTime: "",
    batchDays: [], batchStatus: "", isActive: true, batchStartDate: "", batchEndDate: "",
    onlineClassLink: "",
  });

  const [trainers, setTrainers] = useState([]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/trainers`)
      .then(r => r.json())
      .then(d => Array.isArray(d) && setTrainers(d))
      .catch(console.error);
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (name === "batchStartTime" || name === "batchEndTime") {
      setErrors(p => { const n = { ...p }; delete n.batchTime; return n; });
    } else {
      setErrors(p => ({ ...p, [name]: "" }));
    }
  };

  const toggleDay = (day) => {
    setFormData(p => ({
      ...p,
      batchDays: p.batchDays.includes(day)
        ? p.batchDays.filter(d => d !== day)
        : [...p.batchDays, day],
    }));
    setErrors(p => ({ ...p, batchDays: "" }));
  };

  const validate = () => {
    const e = {};
    if (!formData.batchTitle.trim())       e.batchTitle       = "Batch title is required";
    if (!formData.batchDuration.trim())    e.batchDuration    = "Duration is required";
    if (!formData.batchDescription.trim()) e.batchDescription = "Description is required";
    if (!formData.trainerId)               e.trainerId        = "Trainer is required";
    if (!formData.batchStartTime || !formData.batchEndTime) e.batchTime = "Both start and end time are required";
    if (formData.batchDays.length === 0)   e.batchDays        = "Select at least one day";
    if (!formData.batchStatus.trim())      e.batchStatus      = "Status is required";
    if (formData.batchStartDate && formData.batchEndDate && formData.batchEndDate < formData.batchStartDate)
      e.batchEndDate = "End date cannot be before start date";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    setLoading(true);
    try {
      const batchTime = `${formData.batchStartTime} - ${formData.batchEndTime}`;
      const payload = { ...formData, batchTime };
      delete payload.batchStartTime;
      delete payload.batchEndTime;

      const created = await createBatch(payload);
      const batchId = created._id;

      const tempNotes = JSON.parse(localStorage.getItem("tempNotes") || "[]");
      if (tempNotes.length > 0) {
        const notesPayload = tempNotes.map(n => ({
          ...n, batchId,
          dateTime: n.dateTime || new Date().toISOString(),
          status: n.status || "Enabled",
          createdDateTime: new Date().toISOString(),
          updatedDateTime: new Date().toISOString(),
        }));
        const res = await fetch(`${API_URL}/api/multiplenotes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ notes: notesPayload }),
        });
        if (!res.ok) throw new Error("Failed to save notes");
        localStorage.removeItem("tempNotes");
      }

      navigate("/batches", { state: { successMessage: "Batch created successfully!" } });
    } catch (err) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Creating batch…</p>
        </div>
      )}

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon green">📚</div>
          <div className="af-header-text">
            <h2 className="af-title">Add New Batch</h2>
            <p className="af-subtitle">Configure the batch details, schedule, and notes</p>
          </div>
        </div>

        {successMessage && <div className="af-alert af-alert-success" style={{margin:"16px 32px 0"}}>{successMessage}</div>}
        {errorMessage   && <div className="af-alert af-alert-error"   style={{margin:"16px 32px 0"}}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            {/* Basic Info */}
            <div className="af-section">
              <p className="af-section-title">Batch Information</p>
              <Field label="Batch Title" required error={errors.batchTitle}>
                <input type="text" name="batchTitle" placeholder="e.g. MERN Stack – Batch 12"
                  value={formData.batchTitle} onChange={handleChange}
                  className={errors.batchTitle ? "error-input" : ""} />
              </Field>
              <div className="af-row">
                <Field label="Duration" required error={errors.batchDuration}>
                  <input type="text" name="batchDuration" placeholder="e.g. 3 months"
                    value={formData.batchDuration} onChange={handleChange}
                    className={errors.batchDuration ? "error-input" : ""} />
                </Field>
                <Field label="Trainer" required error={errors.trainerId}>
                  <select name="trainerId" value={formData.trainerId} onChange={handleChange}
                    className={errors.trainerId ? "error-input" : ""}>
                    <option value="">{trainers.length === 0 ? "Loading…" : "Select trainer"}</option>
                    {trainers.map(t => <option key={t._id} value={t._id}>{t.trainerName}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Description" required error={errors.batchDescription}>
                <textarea name="batchDescription" rows={3}
                  placeholder="Brief overview of what this batch covers…"
                  value={formData.batchDescription} onChange={handleChange}
                  className={errors.batchDescription ? "error-input" : ""} />
              </Field>
            </div>

            {/* Schedule */}
            <div className="af-section">
              <p className="af-section-title">Schedule</p>
              <div className="af-field">
                <label className="af-label">Class Time<span className="af-required">*</span></label>
                <div className="af-time-row">
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.5px"}}>From</label>
                    <input type="time" name="batchStartTime" value={formData.batchStartTime}
                      onChange={handleChange} className={errors.batchTime ? "error-input" : ""} />
                  </div>
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.5px"}}>To</label>
                    <input type="time" name="batchEndTime" value={formData.batchEndTime}
                      onChange={handleChange} className={errors.batchTime ? "error-input" : ""} />
                  </div>
                </div>
                {errors.batchTime && <span className="af-error">{errors.batchTime}</span>}
              </div>

              <div className="af-field">
                <label className="af-label">Batch Days<span className="af-required">*</span></label>
                <div className="af-day-grid">
                  {DAYS.map(day => (
                    <span key={day}
                      className={`af-day-chip${formData.batchDays.includes(day) ? " selected" : ""}`}
                      onClick={() => toggleDay(day)}>
                      {day.slice(0, 3)}
                    </span>
                  ))}
                </div>
                {errors.batchDays && <span className="af-error">{errors.batchDays}</span>}
              </div>
            </div>

            {/* Status & Dates */}
            <div className="af-section">
              <p className="af-section-title">Status & Dates</p>
              <Field label="Batch Status" required error={errors.batchStatus}>
                <select name="batchStatus" value={formData.batchStatus} onChange={handleChange}
                  className={errors.batchStatus ? "error-input" : ""}>
                  <option value="">Select status…</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Completed">Completed</option>
                  <option value="Deleted">Deleted</option>
                </select>
              </Field>
                <Field label="Visibility" error={errors.isActive}>
                  <label className="af-switch">
                    <input
                      type="checkbox"
                      checked={formData.isActive !== false}
                      onChange={(e) => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                    />
                    <span>{formData.isActive !== false ? "Active" : "Inactive"}</span>
                  </label>
                  <span className="af-hint">
                    Inactive batches are hidden from lists and filter dropdowns across
                    the Admin and Trainer apps. Nothing is deleted, and enrolled students keep access to theirs.
                  </span>
                </Field>
              <div className="af-row">
                <Field label="Start Date" error={errors.batchStartDate}>
                  <input type="date" name="batchStartDate" value={formData.batchStartDate}
                    onChange={handleChange} />
                </Field>
                <Field label="End Date" error={errors.batchEndDate}>
                  <input type="date" name="batchEndDate" value={formData.batchEndDate}
                    onChange={handleChange} />
                </Field>
              </div>
            </div>

            {/* Online Class */}
            <div className="af-section">
              <p className="af-section-title">Online Class (Optional)</p>
              <Field label="Online Class Link">
                <input type="url" name="onlineClassLink"
                  placeholder="https://meet.google.com/xxx-xxxx-xxx"
                  value={formData.onlineClassLink} onChange={handleChange} />
              </Field>
            </div>

            {/* Notes */}
            <div className="af-section">
              <p className="af-section-title">Notes (Optional)</p>
              <div className="af-action-strip">
                <button type="button" className="af-secondary-btn"
                  onClick={() => setShowNotesDialog(true)}>
                  + Add Notes
                </button>
              </div>
              <NotesTable
                batchId={formData._id}
                openNotesDialogExternally={showNotesDialog}
                setOpenNotesDialogExternally={setShowNotesDialog}
              />
            </div>
          </div>

          <div className="af-submit-row">
            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? "Creating…" : "Create Batch"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBatches;
